import { useEffect, useRef } from 'react';

import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { HighlightStyle, indentUnit, syntaxHighlighting } from '@codemirror/language';
import { linter, lintGutter } from '@codemirror/lint';
import { EditorState, StateField } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { tags } from '@lezer/highlight';
import styled from 'styled-components';

import { FormError } from '@components/form-error';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { asteriskLabel } from '@utils/strings';

const highlightStyle = HighlightStyle.define([
  { tag: tags.keyword, color: theme.color.purple80 },
  { tag: tags.number, color: theme.color.orange100 },
  { tag: tags.string, color: theme.color.green100 },
  { tag: tags.bool, color: theme.color.indigo100 },
  { tag: tags.propertyName, color: theme.color.elementBlue100 },
]);

export type JsonValue = string | number | boolean | null | JsonValue[] | JsonObject;
export type JsonObject = { [key: string]: JsonValue };

export type JsonEditorProps = {
  initialValue?: JsonValue;
  label: string;
  onChange?: (value: string) => void;
  error?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
};

export const JsonEditor = ({
  initialValue,
  onChange,
  label,
  error = undefined,
  isDisabled = false,
  isRequired = false,
}: JsonEditorProps) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current) {
      const initialJson = initialValue ?? {};

      const formattedJson = JSON.stringify(initialJson, null, 2);
      const changeHandler = StateField.define<string>({
        create: () => formattedJson,
        update(value, transaction) {
          if (transaction.docChanged) {
            const newValue = transaction.state.doc.toString();
            return newValue;
          }
          return value;
        },
      });

      const view = new EditorView({
        doc: formattedJson,
        extensions: [
          EditorState.readOnly.of(isDisabled),
          history(),
          keymap.of([...defaultKeymap, indentWithTab, ...historyKeymap]),
          json(),
          syntaxHighlighting(highlightStyle),
          indentUnit.of('  '),
          lineNumbers(),
          linter(jsonParseLinter()),
          lintGutter(),
          changeHandler,
          EditorView.updateListener.of((update) => {
            if (update.docChanged) {
              const currentValue = update.state.field(changeHandler);
              onChange?.(currentValue);
            }
          }),
        ],
        parent: editorRef.current,
      });

      return () => {
        view.destroy();
      };
    }
  }, [initialValue, onChange, isDisabled]);

  return (
    <Stack>
      <Header>
        <Text font="semibold14" text={asteriskLabel(label, isRequired)} />
        <Text font="bold14" color="textHelper" text="JSON" />
      </Header>
      <StyledEditor ref={editorRef} data-error={Boolean(error)} data-is-disabled={isDisabled} />
      <FormError text={error} />
    </Stack>
  );
};

const Header = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
})({
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.uiTrans5,
  border: `1px solid ${theme.color.border10}`,
  borderBottom: 'none',
  borderBottomRightRadius: 0,
  borderBottomLeftRadius: 0,
});

const StyledEditor = styled.div({
  '.cm-content': {
    caretColor: 'auto',
  },
  '.cm-editor': {
    overflow: 'auto',
    maxHeight: pxToRem(384),
    backgroundColor: theme.color.uiTrans5,
    border: `1px solid ${theme.color.border10}`,
    borderBottomRightRadius: pxToRem(8),
    borderBottomLeftRadius: pxToRem(8),
    margin: 0,
    padding: `${theme.space[2]} ${theme.space[3]}`,
    color: theme.form.value.color,

    '&[data-is-disabled="false"]': {
      ':hover': {
        backgroundColor: theme.form.bg.colorHover,
        borderBottomRadius: pxToRem(8),
      },
    },
  },
  '.cm-lintPoint:after': {
    borderBottom: `${pxToRem(4)} solid ${theme.color.red200}`,
  },
  '.cm-gutters': {
    backgroundColor: 'inherit',
    color: theme.color.textHelper,
    border: 'none',
  },

  '&[data-is-disabled="false"] .cm-editor.cm-focused': {
    backgroundColor: theme.form.bg.colorFocus,
    outline: 'none',
  },

  '&[data-is-disabled="false"] .cm-editor.cm-focused .cm-gutters': {
    backgroundColor: theme.form.bg.colorFocus,
  },

  '&[data-is-disabled="true"] .cm-focused': {
    outline: 'none',
  },

  '&[data-error="true"] .cm-editor': {
    borderColor: theme.color.red100,
  },
  '.cm-tooltip': {
    border: 'none',
    borderRadius: '100%',
  },
  '.cm-diagnostic': {
    outline: 'none',
    border: 'none',
    borderBottomRightRadius: pxToRem(8),
    borderBottomLeftRadius: pxToRem(8),
    backgroundColor: theme.color.red100,
    color: theme.color.white100,
    padding: theme.space[4],
  },
  '.cm-gutterElement': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  '.cm-lint-marker-error': {
    content: 'none',
    borderRadius: '100%',
    background: theme.color.red100,
    height: pxToRem(8),
    width: pxToRem(8),
    ':hover': {
      background: theme.color.red200,
    },
  },
});
